import { useState } from "react";
import type {
  DOMAttributes,
  ElementType,
  InputHTMLAttributes,
  ReactNode,
  Ref,
} from "react";
import { cx } from "class-variance-authority";

import { InputError } from "../InputError/InputError";
import { Skeleton } from "../Skeleton/Skeleton";
import { Typography } from "../Typography/Typography";

import * as styles from "./Input.module.scss";

type InputAttribute = DOMAttributes<HTMLInputElement>;

export type InputProps = {
  label?: ReactNode;
  containerClassName?: string;
  inputClassName?: string;
  wrapperClassName?: string;
  errorMessage?: ReactNode;
  hasError?: boolean;
  icon?: ReactNode;
  prefixIcon?: ReactNode;
  loading?: boolean;
  iconContainerClassName?: string;
  inputElement?: ElementType;
  /** Remove the margin from around the element */
  noSpacing?: boolean;
  disableFocusStyling?: boolean;
} & InputHTMLAttributes<HTMLInputElement>;

const Input = ({
  ref,
  id,
  errorMessage,
  label,
  containerClassName,
  inputClassName,
  inputElement,
  hasError,
  icon,
  prefixIcon,
  onFocus,
  onBlur,
  disabled,
  wrapperClassName,
  loading = false,
  iconContainerClassName,
  noSpacing,
  disableFocusStyling = false,
  ...props
}: InputProps & { ref?: Ref<HTMLInputElement> }) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus: InputAttribute["onFocus"] = (event) => {
    setIsFocused(true);
    if (onFocus) onFocus(event);
  };

  const handleBlur: InputAttribute["onBlur"] = (event) => {
    setIsFocused(false);
    if (onBlur) onBlur(event);
  };

  if (props.type === "hidden") {
    return <input type="hidden" {...props} />;
  }

  const InputEl = (inputElement ?? "input") as "input";

  return (
    <div
      className={cx(styles.container, containerClassName, {
        [styles.noSpacing]: noSpacing,
      })}
      data-testid="input-container"
    >
      {label && (
        <Typography
          component="label"
          className={styles.label}
          skeleton={{ loading }}
          htmlFor={id}
          noSpacing
        >
          {label}
        </Typography>
      )}
      {loading ? (
        <Skeleton />
      ) : (
        <>
          <div
            data-testid="input-wrapper"
            className={cx(
              styles.inputWrapper,
              {
                [styles.focus]: isFocused && !disableFocusStyling,
                [styles.hasError]: !!errorMessage || hasError,
                [styles.disabled]: !!disabled,
                [styles.hasIcon]: !!icon,
                [styles.hasPrefixIcon]: !!prefixIcon,
              },
              wrapperClassName,
            )}
          >
            {prefixIcon && (
              <div className={styles.prefixIcon}>{prefixIcon}</div>
            )}
            <InputEl
              id={id}
              className={cx(styles.input, inputClassName)}
              ref={ref}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={disabled}
              {...props}
            />
            {icon && (
              <div className={cx(styles.icon, iconContainerClassName)}>
                {icon}
              </div>
            )}
          </div>
          {errorMessage && <InputError message={errorMessage} />}
        </>
      )}
    </div>
  );
};

Input.displayName = "Input";

export { Input };
